import type { operations } from "@/openapi.gen.ts";
import { runsQueryPrefix } from "@/services/agent-runs-service.ts";
import { AGENT_SCHEDULES_QUERY_PREFIX } from "@/services/agent-schedules-service.ts";
import { agentTriggerInvalidationKeys } from "@/services/agent-triggers-service.ts";
import { $api } from "@/utils/fetch.ts";
import {
	type GetNextPageParamFunction,
	type QueryKey,
	useQueryClient,
} from "@tanstack/react-query";

/**
 * One agent, as every agent surface reads it.
 *
 * Derived from the generated API rather than hand-written, so a change to the
 * endpoint's shape breaks the type here instead of at runtime — the hand-copy
 * this replaces had already lost `icon`. The list rows carry the same shape,
 * which `useAgentDefinitionsInfiniteQuery` below asserts by annotating its pages
 * with it.
 */
export type AgentDefinition =
	operations["api_agent-definitions_agentId_get"]["responses"][200]["content"]["application/json"];

export type AgentDefinitionSort = "name" | "createdAt" | "updatedAt";

export const useAgentDefinitionsInfiniteQuery = ({
	search,
	pageSize,
	sort,
	sortDir,
}: {
	search?: string;
	pageSize?: number;
	sort?: AgentDefinitionSort;
	sortDir?: "asc" | "desc";
}) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		{ agents: AgentDefinition[]; totalCount: number; hasNextPage?: boolean }
	> = (lastPage, _allPages, lastPageParam) => {
		if (lastPage.hasNextPage && typeof lastPageParam === "number") {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/agent-definitions",
		{
			params: {
				query: {
					pageSize,
					search,
					sort,
					sortDir,
				},
			},
		},
		{
			pageParamName: "page",
			initialPageParam: 0,
			getNextPageParam,
		},
	);
};

/** The composer picker's flat list, mirroring `personasQueryOptions`. */
export const agentDefinitionsQueryOptions = () =>
	$api.queryOptions("get", "/api/agent-definitions");

export const agentDefinitionQueryOptions = (agentId: string) =>
	$api.queryOptions("get", "/api/agent-definitions/{agentId}", {
		params: { path: { agentId } },
	});

export interface AgentToolGroup {
	key: string;
	kind: "builtin" | "connector";
	/**
	 * i18n message key for a connector group's heading, resolved server-side from
	 * the connector registry. Absent for built-in groups, whose heading the picker
	 * derives from `key`.
	 */
	labelKey?: string;
	tools: string[];
}

/**
 * The abilities the builder may offer. Server-resolved: which connectors the
 * caller can see depends on their spaces, so the list cannot be a constant.
 */
export const agentToolCatalogueQueryOptions = () =>
	$api.queryOptions("get", "/api/agent-definitions/tool-catalogue", {});

/** Whether the caller may run this agent, and what they lack if not. */
export const agentAccessQueryOptions = (agentId: string) =>
	$api.queryOptions("get", "/api/agent-definitions/{agentId}/access", {
		params: { path: { agentId } },
	});

/**
 * openapi-react-query keys every query as [method, literalPath, init], and
 * TanStack's `partialMatchKey` compares element 2 by exact string. This prefix
 * therefore covers the agents list and its infinite variant whatever
 * search/sort params are in flight — and nothing else. It does NOT reach the
 * detail query ("…/{agentId}") or the org-wide schedules query
 * ("…/schedules"): those are different literal paths, so they need their own
 * keys.
 */
const AGENT_DEFINITIONS_QUERY_PREFIX = ["get", "/api/agent-definitions"];

/**
 * Every query a write to an agent definition leaves stale: the lists, the
 * org-wide schedules panel (each row prints the agent's name), and — for a write
 * that names an agent — its detail query, which the hub, the runs header and the
 * schedule header all read, and its access check, which a save that picks the
 * missing connection (or drops a pin) changes and which gates Run and Chat.
 */
export const agentDefinitionInvalidationKeys = (
	agentId?: string,
): QueryKey[] => [
	AGENT_DEFINITIONS_QUERY_PREFIX,
	AGENT_SCHEDULES_QUERY_PREFIX,
	...(agentId
		? [
				agentDefinitionQueryOptions(agentId).queryKey,
				agentAccessQueryOptions(agentId).queryKey,
			]
		: []),
];

/**
 * Every query an update leaves stale. Beyond the definition's own keys, a save
 * by a non-admin that changes what the agent does pauses the triggers other
 * users created on it and ends the runs those queued (`pausedTriggers`), so the
 * agent's triggers and runs are stale too.
 */
export const agentDefinitionUpdateInvalidationKeys = (
	agentId: string,
): QueryKey[] => [
	...agentDefinitionInvalidationKeys(agentId),
	...agentTriggerInvalidationKeys(agentId),
	runsQueryPrefix(agentId),
];

export const useCreateAgentDefinitionMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/agent-definitions", {
		onSuccess: () => {
			for (const queryKey of agentDefinitionInvalidationKeys()) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

export const useUpdateAgentDefinitionMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/agent-definitions/{agentId}", {
		onSuccess: (_data, variables) => {
			const keys = agentDefinitionUpdateInvalidationKeys(
				variables.params.path.agentId,
			);
			for (const queryKey of keys) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

/**
 * Turn a short job description into clear agent instructions, on Uno's model.
 * Stateless like the description suggestion, so it invalidates nothing.
 */
export const useRefineAgentInstructionsMutation = () =>
	$api.useMutation("post", "/api/agent-definitions/refine-instructions");

export const useDeleteAgentDefinitionMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("delete", "/api/agent-definitions/{agentId}", {
		onSuccess: (_data, variables) => {
			const keys = agentDefinitionInvalidationKeys(
				variables.params.path.agentId,
			);
			for (const queryKey of keys) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};
